import {
  IWebClientMetricsLogger,
  MetricsEvent,
  getFriendlyEventDescription,
} from "./IWebClientMetricsLogger";
import { MetricsSuites } from "./MetricsSuites";
import { PerformanceMarker } from "./PerformanceMarker";

// Logger class to log MetricsEvent to browser's devtool
export class LocalWebClientMetricsLogger implements IWebClientMetricsLogger {
  private static readonly AppStartMarker: string = "AppStartEpochMarker";
  private static readonly EPTStartMarker: string = "EPTStartMarker";
  private static readonly ADJAZNStartMarker: string = "ADJAZNStartMarker";
  private static readonly performanceMarker: PerformanceMarker =
    new PerformanceMarker();

  constructor() {
    // empty function
  }

  public logEvent(evt: MetricsEvent): void {
    const params = evt.parameters;
    const desc = LocalWebClientMetricsLogger.buildDescriptionName(
      params.description,
      params.extraInfo
    );
    let startMarkName: string;
    if (desc === undefined) {
      return;
    }

    if (
      evt.metricSuite === MetricsSuites.Bootstrap &&
      params.elapsed !== undefined &&
      params.elapsed !== -1
    ) {
      if (desc.includes("Experienced Page Load Time")) {
        startMarkName = LocalWebClientMetricsLogger.EPTStartMarker;
      } else if (desc.includes("Adjusted All Zones Loaded")) {
        startMarkName = LocalWebClientMetricsLogger.ADJAZNStartMarker;
      } else {
        startMarkName = LocalWebClientMetricsLogger.AppStartMarker;
      }
    } else {
      startMarkName = LocalWebClientMetricsLogger.buildStartName(desc);
    }
    LocalWebClientMetricsLogger.logLocalMetricEnd(desc, startMarkName);
  }

  public static logLocalMetricStart(metricName: string): string {
    const startMarkName =
      LocalWebClientMetricsLogger.buildStartName(metricName);
    LocalWebClientMetricsLogger.performanceMarker.mark(startMarkName);
    return startMarkName;
  }

  public static markAppStart() {
    LocalWebClientMetricsLogger.performanceMarker.mark(
      LocalWebClientMetricsLogger.AppStartMarker
    );
  }

  public static markEptStart() {
    LocalWebClientMetricsLogger.markStart(
      LocalWebClientMetricsLogger.EPTStartMarker,
      0
    );
  }

  public static markAdjAznStart(startTime: number) {
    LocalWebClientMetricsLogger.markStart(
      LocalWebClientMetricsLogger.ADJAZNStartMarker,
      startTime
    );
  }

  public static markStart(startMarkerName: string, startTime: number) {
    const option: Record<string, number> = { startTime: startTime };
    LocalWebClientMetricsLogger.performanceMarker.mark(startMarkerName, option);
  }

  public static logLocalMetricEnd(
    metricName: string,
    startMarkName: string | null
  ) {
    const endMarkName = LocalWebClientMetricsLogger.buildEndName(metricName);
    if (startMarkName === null) {
      startMarkName = LocalWebClientMetricsLogger.buildStartName(metricName);
    }
    LocalWebClientMetricsLogger.performanceMarker.mark(endMarkName);
    // The "Heavy Greek Cross" symbol is designed to make the measure names clear they are coming from Tableau
    // code
    // React uses ⚛ for the same reason
    LocalWebClientMetricsLogger.performanceMarker.measure(
      "✚ " + metricName,
      endMarkName,
      startMarkName
    );
  }

  public static buildDescriptionName(
    message: string | undefined,
    extraInfo: string | undefined
  ): string | undefined {
    if (message !== undefined) {
      const friendlyEventDescription = getFriendlyEventDescription(message);
      if (friendlyEventDescription === undefined) {
        return message;
      } else if (extraInfo !== null && extraInfo !== undefined) {
        return friendlyEventDescription + ": " + extraInfo;
      } else {
        return friendlyEventDescription;
      }
    }
    return undefined;
  }

  private static buildStartName(desc: string): string {
    return "__start__" + desc;
  }

  private static buildEndName(desc: string): string {
    return "__end__" + desc;
  }
}
